/**
 * HR Engine - Page Campagnes d'Évaluation de Performance
 * Conforme au cahier des charges client
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import {
  Plus,
  Search,
  Calendar,
  Play,
  Archive,
  Eye,
  Loader2,
  Target,
  Users,
  CheckCircle2,
  Clock,
  BarChart3,
  FileText,
  Building2,
  ChevronRight,
  ChevronDown,
  Star,
  Send,
  RefreshCw,
  TrendingUp,
  Award,
  AlertTriangle,
  User,
  UserCheck,
  Key,
  Briefcase,
  Pencil,
  Trash2,
  MoreVertical,
  Power,
  PowerOff,
  Settings,
  Wrench
} from 'lucide-react';

// Configuration des types de campagnes
const CAMPAIGN_TYPES = {
  annual: { 
    label: 'Évaluation annuelle', 
    color: 'bg-blue-100 text-blue-700',
    icon: Calendar,
    description: 'Évaluation complète de fin d\'année'
  },
  midterm: { 
    label: 'Évaluation mi-année', 
    color: 'bg-amber-100 text-amber-700',
    icon: Clock,
    description: 'Point d\'étape semestriel'
  },
  probation: { 
    label: 'Période d\'essai', 
    color: 'bg-purple-100 text-purple-700',
    icon: CheckCircle2,
    description: 'Évaluation de fin de période d\'essai'
  }
};

// Configuration des statuts
const STATUS_CONFIG = {
  draft: { label: 'Brouillon', color: 'bg-slate-100 text-slate-700', dotColor: 'bg-slate-400' },
  active: { label: 'En cours', color: 'bg-green-100 text-green-700', dotColor: 'bg-green-500' },
  self_eval: { label: 'Auto-évaluation', color: 'bg-yellow-100 text-yellow-700', dotColor: 'bg-yellow-500' },
  manager_eval: { label: 'Éval. Manager', color: 'bg-blue-100 text-blue-700', dotColor: 'bg-blue-500' },
  validation: { label: 'Validation', color: 'bg-purple-100 text-purple-700', dotColor: 'bg-purple-500' },
  completed: { label: 'Terminée', color: 'bg-emerald-100 text-emerald-700', dotColor: 'bg-emerald-500' },
  archived: { label: 'Archivée', color: 'bg-gray-100 text-gray-500', dotColor: 'bg-gray-400' }
};

export default function EvaluationCampaignsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [sites, setSites] = useState([]);
  const [societes, setSocietes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [jobProfiles, setJobProfiles] = useState([]);
  const [toValidateEvaluations, setToValidateEvaluations] = useState([]);
  
  // Filtres
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [launchConfirm, setLaunchConfirm] = useState(null);
  const [launching, setLaunching] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [editCampaign, setEditCampaign] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    campaignType: 'annual',
    periodStart: '',
    periodEnd: '',
    selfEvalStartDate: '',
    selfEvalEndDate: '',
    managerEvalStartDate: '',
    managerEvalEndDate: '',
    validationEndDate: '',
    targetDepartmentIds: [],
    targetEmployeeIds: [],
    targetContractTypes: [],
    targetCategories: [],
    targetSiteIds: [],
    targetSocieteIds: [],
    minSeniorityMonths: 0,
    includeN2Validation: false,
    sendNotifications: true
  });
  const [step, setStep] = useState(1);
  const [creating, setCreating] = useState(false);
  const [employeesWithoutAccount, setEmployeesWithoutAccount] = useState([]);
  const [employeesWithoutObjectives, setEmployeesWithoutObjectives] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const companyId = user?.companyId;

      // 1. Charger d'abord les données essentielles pour afficher la page rapidement
      const [campaignsRes, deptsRes, sitesRes, societesRes, toValidateRes] = await Promise.all([
        api.get('/performance-eval/campaigns'),
        api.get('/departments'),
        api.get('/sites').catch(() => ({ data: { sites: [] } })),
        api.get('/societes').catch(() => ({ data: { societes: [] } })),
        api.get('/performance-eval/to-validate').catch(() => ({ data: { evaluations: [] } }))
      ]);

      setCampaigns(campaignsRes.data?.campaigns || []);
      setDepartments(deptsRes.data?.departments || deptsRes.data || []);
      setSites(sitesRes.data?.sites || sitesRes.data || []);
      setSocietes(societesRes.data?.societes || societesRes.data || []);
      setToValidateEvaluations(toValidateRes.data?.evaluations || []);
      setLoading(false); // Débloque l'affichage dès maintenant

      // 2. Charger en arrière-plan les données lourdes (employés + fiches de poste)
      //    Utilisées uniquement dans la boîte de création de campagne
      const heavyPromises = [
        api.get('/employees?limit=10000').catch(() => ({ data: { employees: [] } })),
        companyId
          ? api.get(`/skills/job-profiles?company_id=${companyId}`).catch(() => ({ data: { profiles: [] } }))
          : Promise.resolve({ data: { profiles: [] } })
      ];
      Promise.all(heavyPromises).then(([empsRes, profilesRes]) => {
        setEmployees((empsRes.data?.employees || empsRes.data || []).filter(e => e.status === 'Actif'));
        setJobProfiles(profilesRes.data?.profiles || profilesRes.data?.jobProfiles || []);
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      setCreating(true);
      
      if (!formData.name.trim()) {
        toast.error('Le nom de la campagne est requis');
        return;
      }
      
      if (!formData.periodStart || !formData.periodEnd) {
        toast.error('Les dates de période sont requises');
        return;
      }
      
      // Déterminer les employés ciblés par cette campagne
      let targetedEmployees = [];
      if (formData.targetEmployeeIds?.length > 0) {
        // Employés spécifiques sélectionnés
        targetedEmployees = employees.filter(e => formData.targetEmployeeIds.includes(e.id));
      } else if (formData.targetDepartmentIds?.length > 0) {
        // Employés des départements sélectionnés
        targetedEmployees = employees.filter(e => formData.targetDepartmentIds.includes(e.departmentId));
      } else {
        // Tous les employés actifs
        targetedEmployees = employees;
      }

      // Filtrage additionnel par filtres avancés (contrat / catégorie / site / société / ancienneté)
      targetedEmployees = applyAdvancedFilters(targetedEmployees);

      if (targetedEmployees.length === 0) {
        toast.error("Aucun employé ne correspond aux critères sélectionnés");
        return;
      }
      
      // Vérifier si certains employés n'ont pas de compte utilisateur
      const withoutAccount = targetedEmployees.filter(e => !e.userId);
      
      // Vérifier si certains employés n'ont pas d'objectifs (via leur fiche de poste)
      const withoutObjectives = targetedEmployees.filter(e => {
        if (!e.jobProfileId) return true; // Pas de fiche de poste
        const profile = jobProfiles.find(p => p.id === e.jobProfileId);
        if (!profile) return true; // Fiche non trouvée
        return !profile.objectives || profile.objectives.length === 0; // Pas d'objectifs
      });
      
      // Afficher les avertissements/erreurs
      setEmployeesWithoutAccount(withoutAccount);
      setEmployeesWithoutObjectives(withoutObjectives);
      
      // Les employés sans objectifs restent une erreur bloquante
      if (withoutObjectives.length > 0) {
        toast.error(`${withoutObjectives.length} employé(s) n'ont pas d'objectifs définis`);
        return;
      }
      
      // Politique mai 2026 (option A) : les employés sans compte sont également bloquants.
      // L'auto-évaluation doit obligatoirement être faite par l'employé lui-même.
      if (withoutAccount.length > 0) {
        const sample = withoutAccount
          .slice(0, 3)
          .map(e => `${e.firstName || ''} ${e.lastName || ''}`.trim() || e.matricule || e.id)
          .join(', ');
        const more = withoutAccount.length > 3 ? ` (+${withoutAccount.length - 3} autre(s))` : '';
        toast.error(
          `${withoutAccount.length} employé(s) sans identifiant de connexion ne peuvent pas être évalués : ${sample}${more}. Ajoutez un email, un téléphone ou un matricule sur leur fiche, puis recréez la campagne.`,
          { duration: 8000 }
        );
        return;
      }
      
      // Préparer les données avec les IDs des employés ciblés
      const campaignData = {
        ...formData,
        targetEmployeeIds: targetedEmployees.map(e => e.id),
        // Le mode "proxy" est désactivé depuis mai 2026 — toujours envoyer une liste vide.
        proxyEvaluationEmployeeIds: []
      };
      
      await api.post('/performance-eval/campaigns', campaignData);
      toast.success('Campagne créée avec succès');
      setShowCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const handleLaunch = async (campaignId) => {
    try {
      setLaunching(campaignId);
      const response = await api.post(`/performance-eval/campaigns/${campaignId}/launch`);
      toast.success(`Campagne lancée ! ${response.data.evaluationsCreated} évaluations créées.`);
      setLaunchConfirm(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du lancement');
    } finally {
      setLaunching(null);
    }
  };

  const handleClose = async (campaignId) => {
    try {
      await api.post(`/performance-eval/campaigns/${campaignId}/close`);
      toast.success('Campagne clôturée');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la clôture');
    }
  };

  const handleDelete = async (campaign) => {
    if (!campaign) return;
    try {
      setDeleting(campaign.id);
      await api.delete(`/performance-eval/campaigns/${campaign.id}`);
      toast.success('Campagne supprimée');
      setDeleteConfirm(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeleting(null);
    }
  };

  const handleDeactivate = async (campaign) => {
    try {
      const response = await api.post(`/performance-eval/campaigns/${campaign.id}/deactivate`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la désactivation');
    }
  };

  const handleEditCampaign = async () => {
    if (!editCampaign) return;
    try {
      await api.put(`/performance-eval/campaigns/${editCampaign.id}`, {
        name: editCampaign.name,
        description: editCampaign.description,
        selfEvalEndDate: editCampaign.selfEvalEndDate,
        managerEvalEndDate: editCampaign.managerEvalEndDate,
        validationEndDate: editCampaign.validationEndDate
      });
      toast.success('Campagne mise à jour');
      setEditCampaign(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      campaignType: 'annual',
      periodStart: '',
      periodEnd: '',
      selfEvalStartDate: '',
      selfEvalEndDate: '',
      managerEvalStartDate: '',
      managerEvalEndDate: '',
      validationEndDate: '',
      targetDepartmentIds: [],
      targetEmployeeIds: [],
      targetContractTypes: [],
      targetCategories: [],
      targetSiteIds: [],
      targetSocieteIds: [],
      minSeniorityMonths: 0,
      includeN2Validation: false,
      sendNotifications: true
    });
    setStep(1);
    setEmployeesWithoutAccount([]);
    setEmployeesWithoutObjectives([]);
  };

  // Filtrer les campagnes
  const filteredCampaigns = useMemo(() => {
    return campaigns.filter(c => {
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
      if (typeFilter !== 'all' && c.campaignType !== typeFilter) return false;
      if (searchQuery && !c.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      return true;
    });
  }, [campaigns, statusFilter, typeFilter, searchQuery]);

  // Statistiques
  const stats = useMemo(() => {
    let totalEvals = 0;
    let selfCompleted = 0;
    let managerCompleted = 0;
    let validated = 0;
    let highPerformers = 0;
    let performers = 0;
    let nonPerformers = 0;
    
    campaigns.forEach(c => {
      const s = c.stats || {};
      totalEvals += s.totalParticipants || 0;
      selfCompleted += s.selfEvalCompleted || 0;
      managerCompleted += s.managerEvalCompleted || 0;
      validated += s.validated || 0;
      highPerformers += s.highPerformers || 0;
      performers += s.performers || 0;
      nonPerformers += s.nonPerformers || 0;
    });
    
    return {
      total: campaigns.length,
      active: campaigns.filter(c => ['active', 'self_eval', 'manager_eval', 'validation'].includes(c.status)).length,
      completed: campaigns.filter(c => c.status === 'completed').length,
      totalEvals,
      selfCompleted,
      managerCompleted,
      validated,
      highPerformers,
      performers,
      nonPerformers
    };
  }, [campaigns]);

  const toggleDepartment = (deptId) => {
    const current = formData.targetDepartmentIds;
    if (current.includes(deptId)) {
      setFormData({...formData, targetDepartmentIds: current.filter(id => id !== deptId)});
    } else {
      setFormData({...formData, targetDepartmentIds: [...current, deptId]});
    }
  };

  const toggleContractType = (contractType) => {
    const current = formData.targetContractTypes || [];
    if (current.includes(contractType)) {
      setFormData({...formData, targetContractTypes: current.filter(c => c !== contractType)});
    } else {
      setFormData({...formData, targetContractTypes: [...current, contractType]});
    }
  };

  const toggleArrayField = (field, value) => {
    const current = formData[field] || [];
    if (current.includes(value)) {
      setFormData({...formData, [field]: current.filter(v => v !== value)});
    } else {
      setFormData({...formData, [field]: [...current, value]});
    }
  };

  // Calcule l'ancienneté en mois d'un employé à partir de hireDate
  const monthsSinceHire = (hireDate) => {
    if (!hireDate) return null;
    const d = new Date(hireDate);
    if (Number.isNaN(d.getTime())) return null;
    const now = new Date();
    return (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
  };

  // Applique tous les filtres avancés (contrat, catégorie, site, société, ancienneté)
  const applyAdvancedFilters = (list) => {
    let result = list;
    if (formData.targetContractTypes?.length > 0) {
      result = result.filter(e => formData.targetContractTypes.includes(e.typeContrat || 'CDI'));
    }
    if (formData.targetCategories?.length > 0) {
      result = result.filter(e => formData.targetCategories.includes(e.categorieProfessionnelle || 'Non renseigné'));
    }
    if (formData.targetSiteIds?.length > 0) {
      result = result.filter(e => e.siteId && formData.targetSiteIds.includes(e.siteId));
    }
    if (formData.targetSocieteIds?.length > 0) {
      result = result.filter(e => e.societeId && formData.targetSocieteIds.includes(e.societeId));
    }
    if (formData.minSeniorityMonths > 0) {
      result = result.filter(e => {
        const m = monthsSinceHire(e.hireDate);
        return m !== null && m >= formData.minSeniorityMonths;
      });
    }
    return result;
  };

  // Liste des employés dans le périmètre départemental (utilisée comme base pour
  // les compteurs des filtres avancés). Si aucun département → tous les employés.
  const departmentScopedEmployees = useMemo(() => {
    if (formData.targetDepartmentIds?.length > 0) {
      return employees.filter(e => formData.targetDepartmentIds.includes(e.departmentId));
    }
    return employees;
  }, [employees, formData.targetDepartmentIds]);

  // Liste des types de contrat disponibles (extrait des employés + valeurs standards)
  const availableContractTypes = useMemo(() => {
    const STANDARD = ['CDI', 'CDD', 'Stage', 'Apprentissage', 'Intérim', 'Freelance', 'Consultant'];
    const fromEmployees = new Set();
    employees.forEach(e => {
      const v = (e.typeContrat || '').trim();
      if (v) fromEmployees.add(v);
    });
    STANDARD.forEach(s => fromEmployees.add(s));
    return Array.from(fromEmployees).sort((a, b) => a.localeCompare(b, 'fr'));
  }, [employees]);

  // Liste des catégories professionnelles disponibles
  const availableCategories = useMemo(() => {
    const STANDARD = ['Cadre', 'Agent de maîtrise', 'Ouvrier', 'Employé'];
    const fromEmployees = new Set();
    employees.forEach(e => {
      const v = (e.categorieProfessionnelle || '').trim();
      if (v && v !== 'Non renseigné') fromEmployees.add(v);
    });
    STANDARD.forEach(s => fromEmployees.add(s));
    return Array.from(fromEmployees).sort((a, b) => a.localeCompare(b, 'fr'));
  }, [employees]);

  // Compteur d'employés correspondant au filtre courant
  const targetedEmployeesCount = useMemo(() => {
    let list = employees;
    if (formData.targetDepartmentIds?.length > 0) {
      list = list.filter(e => formData.targetDepartmentIds.includes(e.departmentId));
    }
    return applyAdvancedFilters(list).length;
  }, [employees, formData, applyAdvancedFilters]);

  if (loading) {
    return (
      <DashboardLayout title="Campagnes d'Évaluation">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-webtech-blue" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Évaluation de Performance">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Target className="h-7 w-7 text-indigo-500" />
              Campagnes d'Évaluation
            </h1>
            <p className="text-slate-500 mt-1">
              Gérez les campagnes d'évaluation de performance de vos collaborateurs
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
            <Button variant="outline" onClick={() => navigate('/performance-report')}>
              <BarChart3 className="w-4 h-4 mr-2" />
              Reporting RH
            </Button>
            <Button onClick={() => setShowCreateDialog(true)} className="bg-indigo-600 hover:bg-indigo-700">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle campagne
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <FileText className="w-6 h-6 mx-auto text-slate-400 mb-2" />
                <div className="text-2xl font-bold text-slate-900">{stats.total}</div>
                <p className="text-sm text-slate-500">Campagnes</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-green-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <Play className="w-6 h-6 mx-auto text-green-500 mb-2" />
                <div className="text-2xl font-bold text-green-600">{stats.active}</div>
                <p className="text-sm text-slate-500">En cours</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <Users className="w-6 h-6 mx-auto text-blue-500 mb-2" />
                <div className="text-2xl font-bold text-blue-600">{stats.totalEvals}</div>
                <p className="text-sm text-slate-500">Évaluations</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-emerald-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <Award className="w-6 h-6 mx-auto text-emerald-500 mb-2" />
                <div className="text-2xl font-bold text-emerald-600">{stats.highPerformers}</div>
                <p className="text-sm text-slate-500">High Performers</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <TrendingUp className="w-6 h-6 mx-auto text-blue-500 mb-2" />
                <div className="text-2xl font-bold text-blue-600">{stats.performers}</div>
                <p className="text-sm text-slate-500">Performers</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-red-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <AlertTriangle className="w-6 h-6 mx-auto text-red-500 mb-2" />
                <div className="text-2xl font-bold text-red-600">{stats.nonPerformers}</div>
                <p className="text-sm text-slate-500">Non Performers</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Section: Évaluations à valider */}
        {toValidateEvaluations.length > 0 && (
          <Card className="border-2 border-purple-200 bg-purple-50/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2 text-purple-800">
                <CheckCircle2 className="h-5 w-5" />
                Évaluations à valider ({toValidateEvaluations.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {toValidateEvaluations.map((evaluation) => (
                  <div 
                    key={evaluation.id}
                    className="flex items-center justify-between p-3 bg-white rounded-lg border border-purple-100 hover:border-purple-300 transition-colors cursor-pointer"
                    onClick={() => navigate(`/rh-validation/${evaluation.id}`)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-medium">
                        {evaluation.employee?.firstName?.[0]}{evaluation.employee?.lastName?.[0]}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">
                          {evaluation.employee?.firstName} {evaluation.employee?.lastName}
                        </p>
                        <p className="text-xs text-slate-500">
                          Score manager: <span className="font-semibold text-amber-600">{evaluation.managerScore || 0}/100</span>
                        </p>
                      </div>
                    </div>
                    <Button size="sm" className="bg-purple-600 hover:bg-purple-700">
                      Valider
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input 
              placeholder="Rechercher une campagne..." 
              className="pl-9"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              <SelectItem value="draft">Brouillon</SelectItem>
              <SelectItem value="active">En cours</SelectItem>
              <SelectItem value="completed">Terminée</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les types</SelectItem>
              <SelectItem value="annual">Annuelle</SelectItem>
              <SelectItem value="midterm">Mi-année</SelectItem>
              <SelectItem value="probation">Période d'essai</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Campaigns Grid */}
        {filteredCampaigns.length === 0 ? (
          <Card className="py-12">
            <CardContent className="text-center">
              <Target className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                {campaigns.length === 0 ? 'Aucune campagne créée' : 'Aucune campagne trouvée'}
              </h3>
              <p className="text-slate-500 mb-4">
                {campaigns.length === 0 
                  ? 'Créez votre première campagne d\'évaluation de performance'
                  : 'Modifiez vos filtres pour voir plus de résultats'
                }
              </p>
              {campaigns.length === 0 && (
                <Button onClick={() => setShowCreateDialog(true)} className="bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Créer une campagne
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCampaigns.map((campaign) => {
              const type = CAMPAIGN_TYPES[campaign.campaignType] || CAMPAIGN_TYPES.annual;
              const status = STATUS_CONFIG[campaign.status] || STATUS_CONFIG.draft;
              const TypeIcon = type.icon;
              const s = campaign.stats || {};
              const progress = s.totalParticipants > 0 
                ? Math.round((s.validated / s.totalParticipants) * 100) 
                : 0;
              
              return (
                <Card 
                  key={campaign.id} 
                  className="hover:shadow-md transition-all cursor-pointer"
                  onClick={() => navigate(`/performance/campaign/${campaign.id}`)}
                >
                  <CardContent className="p-4">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${type.color.replace('text-', 'bg-').split(' ')[0]}`}>
                          <TypeIcon className={`w-5 h-5 ${type.color.split(' ')[1]}`} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">{campaign.name}</h3>
                          <Badge variant="outline" className={type.color}>
                            {type.label}
                          </Badge>
                        </div>
                      </div>
                      <Badge className={status.color}>
                        <span className={`w-2 h-2 rounded-full ${status.dotColor} mr-1.5`}></span>
                        {status.label}
                      </Badge>
                    </div>
                    
                    {/* Period */}
                    <div className="text-sm text-slate-500 mb-3 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {new Date(campaign.periodStart).toLocaleDateString('fr-FR')} → {new Date(campaign.periodEnd).toLocaleDateString('fr-FR')}
                    </div>
                    
                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-2 mb-3 py-2 bg-slate-50 rounded-lg px-2">
                      <div className="text-center">
                        <p className="text-lg font-bold text-slate-900">{s.totalParticipants || 0}</p>
                        <p className="text-xs text-slate-500">Participants</p>
                      </div>
                      <div className="text-center border-l border-slate-200">
                        <p className="text-lg font-bold text-blue-600">{s.managerEvalCompleted || 0}</p>
                        <p className="text-xs text-slate-500">Évaluations</p>
                      </div>
                      <div className="text-center border-l border-slate-200">
                        <p className="text-lg font-bold text-green-600">{s.validated || 0}</p>
                        <p className="text-xs text-slate-500">Validées</p>
                      </div>
                    </div>
                    
                    {/* Performance breakdown */}
                    {(s.highPerformers > 0 || s.performers > 0 || s.nonPerformers > 0) && (
                      <div className="flex gap-2 mb-3">
                        {s.highPerformers > 0 && (
                          <Badge className="bg-emerald-100 text-emerald-700">
                            <Star className="w-3 h-3 mr-1" />
                            {s.highPerformers} High
                          </Badge>
                        )}
                        {s.performers > 0 && (
                          <Badge className="bg-blue-100 text-blue-700">
                            {s.performers} Perf.
                          </Badge>
                        )}
                        {s.nonPerformers > 0 && (
                          <Badge className="bg-red-100 text-red-700">
                            {s.nonPerformers} Non
                          </Badge>
                        )}
                      </div>
                    )}
                    
                    {/* Progress */}
                    <div className="mb-3">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-600">Progression</span>
                        <span className="font-medium">{progress}%</span>
                      </div>
                      <Progress value={progress} className="h-2" />
                    </div>
                    
                    {/* Actions */}
                    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                      {campaign.status === 'draft' && (
                        <Button 
                          size="sm" 
                          className="flex-1 bg-green-600 hover:bg-green-700"
                          onClick={() => setLaunchConfirm(campaign)}
                          disabled={launching === campaign.id}
                        >
                          {launching === campaign.id ? (
                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          ) : (
                            <Send className="w-4 h-4 mr-1" />
                          )}
                          Lancer
                        </Button>
                      )}
                      
                      {['active', 'self_eval', 'manager_eval', 'validation'].includes(campaign.status) && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="flex-1"
                          onClick={() => handleClose(campaign.id)}
                        >
                          <Archive className="w-4 h-4 mr-1" />
                          Clôturer
                        </Button>
                      )}
                      
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => navigate(`/performance/campaign/${campaign.id}`)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Détails
                      </Button>
                      
                      {/* Menu déroulant pour plus d'actions */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="ghost">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setEditCampaign({...campaign})}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Modifier
                          </DropdownMenuItem>
                          {campaign.status !== 'archived' ? (
                            <DropdownMenuItem onClick={() => handleDeactivate(campaign)}>
                              <PowerOff className="w-4 h-4 mr-2" />
                              Désactiver
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => handleDeactivate(campaign)}>
                              <Power className="w-4 h-4 mr-2" />
                              Réactiver
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => setDeleteConfirm(campaign)}
                            className="text-red-600 focus:text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Create Campaign Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={(open) => {
          if (!open) {
            setShowCreateDialog(false);
            resetForm();
          }
        }}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-indigo-500" />
                Créer une campagne d'évaluation
              </DialogTitle>
            </DialogHeader>
            
            {/* Steps indicator */}
            <div className="flex items-center gap-2 mb-4">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center">
                  <div 
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      step >= s ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-500'
                    }`}
                  >
                    {s}
                  </div>
                  {s < 3 && (
                    <div className={`w-12 h-1 mx-1 ${step > s ? 'bg-indigo-600' : 'bg-slate-200'}`} />
                  )}
                </div>
              ))}
            </div>
            
            {/* Step 1: Informations générales */}
            {step === 1 && (
              <div className="space-y-4">
                <h3 className="font-medium text-slate-900">Informations générales</h3>
                
                <div className="space-y-2">
                  <Label>Nom de la campagne *</Label>
                  <Input
                    placeholder="Ex: Évaluation de performance 2025"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    placeholder="Description de la campagne..."
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows={2}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Type de campagne</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(CAMPAIGN_TYPES).map(([key, type]) => {
                      const Icon = type.icon;
                      return (
                        <div
                          key={key}
                          className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                            formData.campaignType === key 
                              ? 'border-indigo-500 bg-indigo-50' 
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                          onClick={() => setFormData({...formData, campaignType: key})}
                        >
                          <Icon className={`w-5 h-5 mb-2 ${formData.campaignType === key ? 'text-indigo-600' : 'text-slate-400'}`} />
                          <p className="font-medium text-sm">{type.label}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Début de période évaluée *</Label>
                    <Input
                      type="date"
                      value={formData.periodStart}
                      onChange={(e) => setFormData({...formData, periodStart: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Fin de période évaluée *</Label>
                    <Input
                      type="date"
                      value={formData.periodEnd}
                      onChange={(e) => setFormData({...formData, periodEnd: e.target.value})}
                    />
                  </div>
                </div>
              </div>
            )}
            
            {/* Step 2: Planning */}
            {step === 2 && (
              <div className="space-y-4">
                <h3 className="font-medium text-slate-900">Planning du workflow</h3>
                
                <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                  {/* Étape 1: Auto-évaluation */}
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center text-sm font-medium text-yellow-700 shrink-0">1</div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900 mb-2">Période d'auto-évaluation</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Date début</Label>
                          <Input
                            type="date"
                            value={formData.selfEvalStartDate}
                            onChange={(e) => setFormData({...formData, selfEvalStartDate: e.target.value})}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Date fin</Label>
                          <Input
                            type="date"
                            value={formData.selfEvalEndDate}
                            onChange={(e) => setFormData({...formData, selfEvalEndDate: e.target.value})}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Étape 2: Évaluation manager */}
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-sm font-medium text-blue-700 shrink-0">2</div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900 mb-2">Période d'évaluation manager (N+1)</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Date début</Label>
                          <Input
                            type="date"
                            value={formData.managerEvalStartDate}
                            onChange={(e) => setFormData({...formData, managerEvalStartDate: e.target.value})}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Date fin</Label>
                          <Input
                            type="date"
                            value={formData.managerEvalEndDate}
                            onChange={(e) => setFormData({...formData, managerEvalEndDate: e.target.value})}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Étape 3: Validation */}
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-sm font-medium text-green-700 shrink-0">3</div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900 mb-2">Date limite de validation</p>
                      <div className="space-y-1">
                        <Input
                          type="date"
                          value={formData.validationEndDate}
                          onChange={(e) => setFormData({...formData, validationEndDate: e.target.value})}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Options */}
                <div className="space-y-3 p-4 bg-indigo-50 rounded-lg border border-indigo-100">
                  <h4 className="font-medium text-indigo-900">Options</h4>
                  
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="includeN2"
                      checked={formData.includeN2Validation}
                      onCheckedChange={(checked) => setFormData({...formData, includeN2Validation: checked})}
                    />
                    <Label htmlFor="includeN2" className="font-normal cursor-pointer">
                      Inclure la validation N+2 (si existant)
                    </Label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="sendNotif"
                      checked={formData.sendNotifications}
                      onCheckedChange={(checked) => setFormData({...formData, sendNotifications: checked})}
                    />
                    <Label htmlFor="sendNotif" className="font-normal cursor-pointer">
                      Envoyer les notifications par email
                    </Label>
                  </div>
                </div>
              </div>
            )}
            
            {/* Step 3: Cibles */}
            {step === 3 && (
              <div className="space-y-4">
                <h3 className="font-medium text-slate-900">Employés concernés</h3>
                
                <p className="text-sm text-slate-500">
                  Sélectionnez les départements à inclure. Si aucun n'est sélectionné, tous les employés actifs seront inclus.
                </p>
                
                <div className="max-h-64 overflow-y-auto border rounded-lg p-2 space-y-2">
                  {departments.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-4">Aucun département</p>
                  ) : (
                    departments.map((dept) => (
                      <div
                        key={dept.id}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                          formData.targetDepartmentIds.includes(dept.id)
                            ? 'bg-indigo-50 border border-indigo-200'
                            : 'hover:bg-slate-50 border border-transparent'
                        }`}
                        onClick={() => toggleDepartment(dept.id)}
                      >
                        <Checkbox checked={formData.targetDepartmentIds.includes(dept.id)} />
                        <Building2 className="w-4 h-4 text-slate-400" />
                        <span className="font-medium flex-1">{dept.name}</span>
                        <Badge variant="outline">
                          {employees.filter(e => e.departmentId === dept.id).length} employés
                        </Badge>
                      </div>
                    ))
                  )}
                </div>
                
                {formData.targetDepartmentIds.length > 0 ? (
                  <p className="text-sm text-indigo-600">
                    {formData.targetDepartmentIds.length} département(s) sélectionné(s) - 
                    ~{employees.filter(e => formData.targetDepartmentIds.includes(e.departmentId)).length} employés
                  </p>
                ) : (
                  <p className="text-sm text-slate-500">
                    Tous les employés actifs seront inclus ({employees.length} employés)
                  </p>
                )}

                {/* === FILTRES AVANCÉS === */}
                <div className="mt-4 pt-4 border-t border-slate-200 space-y-4">
                  <div>
                    <h3 className="font-semibold text-slate-900 text-base">Filtres avancés (optionnels)</h3>
                    <p className="text-sm text-slate-500">
                      Affinez la cible. Les compteurs reflètent le périmètre <strong>{formData.targetDepartmentIds?.length > 0 ? 'des départements sélectionnés' : 'de tous les employés actifs'}</strong>. Plusieurs filtres peuvent être combinés.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Filtre par type de contrat */}
                    <div>
                      <Label className="font-medium text-slate-900 mb-2 block">Type de contrat</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            data-testid="contract-types-filter"
                            className="w-full justify-between font-normal"
                          >
                            <span className="truncate text-left">
                              {(formData.targetContractTypes?.length || 0) === 0
                                ? 'Tous les types'
                                : `${formData.targetContractTypes.length} type(s) sélectionné(s)`}
                            </span>
                            <ChevronDown className="w-4 h-4 ml-2 opacity-60 flex-shrink-0" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[280px] p-0" align="start">
                          <div className="max-h-72 overflow-y-auto p-1">
                            {availableContractTypes.map((ct) => {
                              const checked = (formData.targetContractTypes || []).includes(ct);
                              const count = departmentScopedEmployees.filter(e => (e.typeContrat || 'CDI') === ct).length;
                              return (
                                <button
                                  key={ct}
                                  type="button"
                                  data-testid={`contract-type-${ct}`}
                                  onClick={() => toggleContractType(ct)}
                                  className={`w-full flex items-center gap-2 px-2 py-2 rounded-md text-sm transition-colors text-left ${
                                    checked ? 'bg-indigo-50 text-indigo-900' : 'hover:bg-slate-100 text-slate-700'
                                  }`}
                                >
                                  <Checkbox checked={checked} className="pointer-events-none" />
                                  <span className="flex-1 font-medium">{ct}</span>
                                  <Badge variant="outline" className="text-xs">{count}</Badge>
                                </button>
                              );
                            })}
                          </div>
                          {(formData.targetContractTypes?.length || 0) > 0 && (
                            <div className="border-t p-1">
                              <button
                                type="button"
                                onClick={() => setFormData({...formData, targetContractTypes: []})}
                                className="w-full text-xs text-slate-500 hover:text-slate-700 py-1.5"
                              >
                                Effacer la sélection
                              </button>
                            </div>
                          )}
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* Filtre par catégorie professionnelle */}
                    <div>
                      <Label className="font-medium text-slate-900 mb-2 block">Catégorie professionnelle</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            data-testid="categories-filter"
                            className="w-full justify-between font-normal"
                          >
                            <span className="truncate text-left">
                              {(formData.targetCategories?.length || 0) === 0
                                ? 'Toutes les catégories'
                                : `${formData.targetCategories.length} catégorie(s) sélectionnée(s)`}
                            </span>
                            <ChevronDown className="w-4 h-4 ml-2 opacity-60 flex-shrink-0" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[280px] p-0" align="start">
                          <div className="max-h-72 overflow-y-auto p-1">
                            {availableCategories.map((cat) => {
                              const checked = (formData.targetCategories || []).includes(cat);
                              const count = departmentScopedEmployees.filter(e => (e.categorieProfessionnelle || 'Non renseigné') === cat).length;
                              return (
                                <button
                                  key={cat}
                                  type="button"
                                  data-testid={`category-${cat}`}
                                  onClick={() => toggleArrayField('targetCategories', cat)}
                                  className={`w-full flex items-center gap-2 px-2 py-2 rounded-md text-sm transition-colors text-left ${
                                    checked ? 'bg-indigo-50 text-indigo-900' : 'hover:bg-slate-100 text-slate-700'
                                  }`}
                                >
                                  <Checkbox checked={checked} className="pointer-events-none" />
                                  <span className="flex-1 font-medium">{cat}</span>
                                  <Badge variant="outline" className="text-xs">{count}</Badge>
                                </button>
                              );
                            })}
                          </div>
                          {(formData.targetCategories?.length || 0) > 0 && (
                            <div className="border-t p-1">
                              <button
                                type="button"
                                onClick={() => setFormData({...formData, targetCategories: []})}
                                className="w-full text-xs text-slate-500 hover:text-slate-700 py-1.5"
                              >
                                Effacer la sélection
                              </button>
                            </div>
                          )}
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* Filtre par société */}
                    {societes.length > 0 && (
                      <div>
                        <Label className="font-medium text-slate-900 mb-2 block">Société</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              data-testid="societes-filter"
                              className="w-full justify-between font-normal"
                            >
                              <span className="truncate text-left">
                                {(formData.targetSocieteIds?.length || 0) === 0
                                  ? 'Toutes les sociétés'
                                  : `${formData.targetSocieteIds.length} société(s) sélectionnée(s)`}
                              </span>
                              <ChevronDown className="w-4 h-4 ml-2 opacity-60 flex-shrink-0" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[280px] p-0" align="start">
                            <div className="max-h-72 overflow-y-auto p-1">
                              {societes.map((soc) => {
                                const checked = (formData.targetSocieteIds || []).includes(soc.id);
                                const count = departmentScopedEmployees.filter(e => e.societeId === soc.id).length;
                                return (
                                  <button
                                    key={soc.id}
                                    type="button"
                                    data-testid={`societe-${soc.id}`}
                                    onClick={() => toggleArrayField('targetSocieteIds', soc.id)}
                                    className={`w-full flex items-center gap-2 px-2 py-2 rounded-md text-sm transition-colors text-left ${
                                      checked ? 'bg-indigo-50 text-indigo-900' : 'hover:bg-slate-100 text-slate-700'
                                    }`}
                                  >
                                    <Checkbox checked={checked} className="pointer-events-none" />
                                    <span className="flex-1 font-medium truncate">{soc.name}</span>
                                    <Badge variant="outline" className="text-xs">{count}</Badge>
                                  </button>
                                );
                              })}
                            </div>
                            {(formData.targetSocieteIds?.length || 0) > 0 && (
                              <div className="border-t p-1">
                                <button
                                  type="button"
                                  onClick={() => setFormData({...formData, targetSocieteIds: []})}
                                  className="w-full text-xs text-slate-500 hover:text-slate-700 py-1.5"
                                >
                                  Effacer la sélection
                                </button>
                              </div>
                            )}
                          </PopoverContent>
                        </Popover>
                      </div>
                    )}

                    {/* Filtre par site */}
                    {sites.length > 0 && (
                      <div>
                        <Label className="font-medium text-slate-900 mb-2 block">Site</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              data-testid="sites-filter"
                              className="w-full justify-between font-normal"
                            >
                              <span className="truncate text-left">
                                {(formData.targetSiteIds?.length || 0) === 0
                                  ? 'Tous les sites'
                                  : `${formData.targetSiteIds.length} site(s) sélectionné(s)`}
                              </span>
                              <ChevronDown className="w-4 h-4 ml-2 opacity-60 flex-shrink-0" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[280px] p-0" align="start">
                            <div className="max-h-72 overflow-y-auto p-1">
                              {sites.map((site) => {
                                const checked = (formData.targetSiteIds || []).includes(site.id);
                                const count = departmentScopedEmployees.filter(e => e.siteId === site.id).length;
                                return (
                                  <button
                                    key={site.id}
                                    type="button"
                                    data-testid={`site-${site.id}`}
                                    onClick={() => toggleArrayField('targetSiteIds', site.id)}
                                    className={`w-full flex items-center gap-2 px-2 py-2 rounded-md text-sm transition-colors text-left ${
                                      checked ? 'bg-indigo-50 text-indigo-900' : 'hover:bg-slate-100 text-slate-700'
                                    }`}
                                  >
                                    <Checkbox checked={checked} className="pointer-events-none" />
                                    <span className="flex-1 font-medium truncate">{site.name}</span>
                                    <Badge variant="outline" className="text-xs">{count}</Badge>
                                  </button>
                                );
                              })}
                            </div>
                            {(formData.targetSiteIds?.length || 0) > 0 && (
                              <div className="border-t p-1">
                                <button
                                  type="button"
                                  onClick={() => setFormData({...formData, targetSiteIds: []})}
                                  className="w-full text-xs text-slate-500 hover:text-slate-700 py-1.5"
                                >
                                  Effacer la sélection
                                </button>
                              </div>
                            )}
                          </PopoverContent>
                        </Popover>
                      </div>
                    )}

                    {/* Filtre par ancienneté minimale */}
                    <div>
                      <Label className="font-medium text-slate-900 mb-2 block">Ancienneté minimale</Label>
                      <Select
                        value={String(formData.minSeniorityMonths || 0)}
                        onValueChange={(v) => setFormData({...formData, minSeniorityMonths: parseInt(v, 10) || 0})}
                      >
                        <SelectTrigger className="w-full" data-testid="seniority-filter">
                          <SelectValue placeholder="Aucune" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0" data-testid="seniority-0">Aucune</SelectItem>
                          <SelectItem value="3" data-testid="seniority-3">≥ 3 mois</SelectItem>
                          <SelectItem value="6" data-testid="seniority-6">≥ 6 mois</SelectItem>
                          <SelectItem value="12" data-testid="seniority-12">≥ 1 an</SelectItem>
                          <SelectItem value="24" data-testid="seniority-24">≥ 2 ans</SelectItem>
                          <SelectItem value="36" data-testid="seniority-36">≥ 3 ans</SelectItem>
                          <SelectItem value="60" data-testid="seniority-60">≥ 5 ans</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Synthèse globale des filtres */}
                  {(formData.targetDepartmentIds?.length > 0
                    || formData.targetContractTypes?.length > 0
                    || formData.targetCategories?.length > 0
                    || formData.targetSiteIds?.length > 0
                    || formData.targetSocieteIds?.length > 0
                    || formData.minSeniorityMonths > 0) && (
                    <div
                      className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg"
                      data-testid="advanced-filters-summary"
                    >
                      <p className="text-sm text-indigo-700">
                        <strong>{targetedEmployeesCount} employé(s)</strong> correspondent à tous les critères sélectionnés.
                        {targetedEmployeesCount === 0 && (
                          <span className="block mt-1 text-red-600 font-medium">
                            Aucun employé ne correspond — la création sera bloquée tant que les filtres seront aussi restrictifs.
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Info si des employés n'ont pas de compte - Évaluation par le manager */}
                {employeesWithoutAccount.length > 0 && (
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <UserCheck className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="font-medium text-blue-800">
                          {employeesWithoutAccount.length} employé(s) sans compte utilisateur
                        </h4>
                        <p className="text-sm text-blue-600 mt-1">
                          Ces employés (ouvriers, personnel terrain...) seront évalués directement par leur manager 
                          qui remplira l'auto-évaluation en leur nom.
                        </p>
                        <div className="mt-3 max-h-32 overflow-y-auto space-y-1">
                          {employeesWithoutAccount.slice(0, 10).map(emp => (
                            <div key={emp.id} className="flex items-center gap-2 text-sm">
                              <User className="w-4 h-4 text-blue-400" />
                              <span className="text-blue-700">
                                {emp.firstName} {emp.lastName} - {emp.jobTitle || 'Poste non défini'}
                              </span>
                              <Badge variant="outline" className="ml-auto text-xs border-blue-300 text-blue-600">
                                Via manager
                              </Badge>
                            </div>
                          ))}
                          {employeesWithoutAccount.length > 10 && (
                            <p className="text-xs text-blue-500 italic">
                              ... et {employeesWithoutAccount.length - 10} autre(s)
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Alerte si des employés n'ont pas d'objectifs */}
                {employeesWithoutObjectives.length > 0 && (
                  <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Target className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-amber-800">
                          {employeesWithoutObjectives.length} employé(s) sans objectifs définis
                        </h4>
                        <p className="text-sm text-amber-600 mt-1">
                          Ces employés n'ont pas de fiche de poste avec des objectifs SMART. 
                          Assignez-leur une fiche de poste ou définissez leurs objectifs.
                        </p>
                        <div className="mt-3 max-h-32 overflow-y-auto space-y-1">
                          {employeesWithoutObjectives.slice(0, 10).map(emp => {
                            const profile = jobProfiles.find(p => p.id === emp.jobProfileId);
                            return (
                              <div key={emp.id} className="flex items-center gap-2 text-sm">
                                <User className="w-4 h-4 text-amber-400" />
                                <span className="text-amber-700">
                                  {emp.firstName} {emp.lastName}
                                  {emp.jobProfileId ? (
                                    <span className="text-amber-500"> - Fiche "{profile?.name || 'inconnue'}" sans objectifs</span>
                                  ) : (
                                    <span className="text-amber-500"> - Pas de fiche de poste</span>
                                  )}
                                </span>
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="text-amber-600 h-auto p-0 ml-auto"
                                  onClick={() => {
                                    if (emp.jobProfileId) {
                                      navigate('/skills/profiles');
                                    } else {
                                      navigate(`/employees/${emp.id}/edit`);
                                    }
                                  }}
                                >
                                  <Briefcase className="w-3 h-3 mr-1" />
                                  {emp.jobProfileId ? 'Ajouter objectifs' : 'Assigner fiche'}
                                </Button>
                              </div>
                            );
                          })}
                          {employeesWithoutObjectives.length > 10 && (
                            <p className="text-xs text-amber-500 italic">
                              ... et {employeesWithoutObjectives.length - 10} autre(s)
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <DialogFooter className="gap-2">
              {step > 1 && (
                <Button variant="outline" onClick={() => setStep(step - 1)}>
                  Précédent
                </Button>
              )}
              
              {step < 3 ? (
                <Button onClick={() => setStep(step + 1)} className="bg-indigo-600 hover:bg-indigo-700">
                  Suivant
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button 
                  onClick={handleCreate} 
                  className="bg-green-600 hover:bg-green-700"
                  disabled={creating}
                >
                  {creating ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  )}
                  Créer la campagne
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Launch Confirmation Dialog */}
        <AlertDialog open={!!launchConfirm} onOpenChange={(open) => !open && setLaunchConfirm(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Lancer la campagne ?</AlertDialogTitle>
              <AlertDialogDescription>
                Êtes-vous sûr de vouloir lancer la campagne "{launchConfirm?.name}" ?
                <br /><br />
                Cette action va :
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Créer les formulaires d'évaluation pour tous les employés ciblés</li>
                  <li>Envoyer les notifications par email (si activé)</li>
                  <li>Permettre aux employés de faire leur auto-évaluation</li>
                </ul>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handleLaunch(launchConfirm?.id)}
                className="bg-green-600 hover:bg-green-700"
              >
                <Send className="w-4 h-4 mr-2" />
                Lancer la campagne
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="text-red-600">Supprimer la campagne ?</AlertDialogTitle>
              <AlertDialogDescription>
                Êtes-vous sûr de vouloir supprimer la campagne <strong>"{deleteConfirm?.name}"</strong> ?
                <br /><br />
                {deleteConfirm?.status === 'archived' ? (
                  <span className="text-red-600 font-medium">
                    ⚠️ Cette campagne est archivée. La suppression entraînera la perte définitive de
                    <strong> toutes les évaluations liées</strong> (auto-évaluations, évaluations manager,
                    validations RH) ainsi que des synthèses IA associées.
                  </span>
                ) : (
                  <span className="text-amber-600 font-medium">
                    ⚠️ Important : Si des évaluations ont déjà été démarrées (auto-évaluation ou évaluation manager soumises),
                    la suppression sera bloquée. Archivez d'abord la campagne si vous souhaitez forcer sa suppression.
                  </span>
                )}
                <br /><br />
                Cette action est irréversible.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting === deleteConfirm?.id}>Annuler</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handleDelete(deleteConfirm)}
                className="bg-red-600 hover:bg-red-700"
                disabled={deleting === deleteConfirm?.id}
              >
                {deleting === deleteConfirm?.id ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Edit Campaign Dialog */}
        <Dialog open={!!editCampaign} onOpenChange={(open) => !open && setEditCampaign(null)}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Pencil className="w-5 h-5 text-blue-500" />
                Modifier la campagne
              </DialogTitle>
            </DialogHeader>
            
            {editCampaign && (
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Nom de la campagne</Label>
                  <Input
                    value={editCampaign.name || ''}
                    onChange={(e) => setEditCampaign({...editCampaign, name: e.target.value})}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={editCampaign.description || ''}
                    onChange={(e) => setEditCampaign({...editCampaign, description: e.target.value})}
                    rows={3}
                  />
                </div>
                
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Dates de fin (extensibles)
                  </h4>
                  <p className="text-sm text-slate-500 mb-3">
                    Vous pouvez prolonger les dates de fin même si des évaluations ont déjà commencé.
                  </p>
                  
                  <div className="grid grid-cols-1 gap-4">
                    <div className="space-y-2">
                      <Label>Fin auto-évaluation</Label>
                      <Input
                        type="date"
                        value={editCampaign.selfEvalEndDate?.split('T')[0] || ''}
                        onChange={(e) => setEditCampaign({...editCampaign, selfEvalEndDate: e.target.value})}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Fin évaluation manager</Label>
                      <Input
                        type="date"
                        value={editCampaign.managerEvalEndDate?.split('T')[0] || ''}
                        onChange={(e) => setEditCampaign({...editCampaign, managerEvalEndDate: e.target.value})}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Fin validation</Label>
                      <Input
                        type="date"
                        value={editCampaign.validationEndDate?.split('T')[0] || ''}
                        onChange={(e) => setEditCampaign({...editCampaign, validationEndDate: e.target.value})}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditCampaign(null)}>
                Annuler
              </Button>
              <Button onClick={handleEditCampaign} className="bg-blue-600 hover:bg-blue-700">
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
